import { TAXONOMY_SEPARATOR, toParamList } from '../../../util/listingTaxonomy';

/**
 * Selection state of the category/type filter, as it lives in the URL:
 *
 *   categories  whole categories              ?category=powerBoats
 *   types       `category/type` filter values  ?type=sailingBoats/yacht
 *
 * A category is either whole, narrowed to some of its types, or not picked; it
 * is never both whole and narrowed. That is the same rule the search query
 * applies (util/listingTaxonomy.js buildCategoryTypeQuery), so what the filter
 * shows ticked is exactly what is searched.
 */
export const CATEGORY_STATE = {
  WHOLE: 'whole',
  PARTIAL: 'partial',
  NONE: 'none',
};

export const categoryOfType = typeValue => typeValue?.split(TAXONOMY_SEPARATOR)[0];

const uniq = values => [...new Set(values)];

export const readSelection = ({ category, type }) => {
  const types = uniq(toParamList(type).filter(v => v.includes(TAXONOMY_SEPARATOR)));
  const narrowed = new Set(types.map(categoryOfType));
  return {
    categories: uniq(toParamList(category)).filter(c => !narrowed.has(c)),
    types,
  };
};

export const getCategoryState = (selection, category) => {
  if (selection.categories.includes(category)) return CATEGORY_STATE.WHOLE;
  if (selection.types.some(t => categoryOfType(t) === category)) return CATEGORY_STATE.PARTIAL;
  return CATEGORY_STATE.NONE;
};

/** Whole → off. Narrowed or off → whole, dropping its types. */
export const toggleCategory = (selection, category) =>
  getCategoryState(selection, category) === CATEGORY_STATE.WHOLE
    ? { ...selection, categories: selection.categories.filter(c => c !== category) }
    : {
        categories: [...selection.categories, category],
        types: selection.types.filter(t => categoryOfType(t) !== category),
      };

/**
 * Ticking a type narrows its category to the ticked types; unticking the last
 * one leaves the category unpicked rather than falling back to whole.
 */
export const toggleType = (selection, typeValue) => {
  if (selection.types.includes(typeValue)) {
    return { ...selection, types: selection.types.filter(t => t !== typeValue) };
  }
  const category = categoryOfType(typeValue);
  return {
    categories: selection.categories.filter(c => c !== category),
    types: [...selection.types, typeValue],
  };
};

/** The category that decides which SEO category page the visitor ends up on. */
export const getPrimaryCategory = selection =>
  selection.categories[0] || categoryOfType(selection.types[0]) || null;

export const getSelectionCount = selection => selection.categories.length + selection.types.length;
